import AttachmentPreview from '@common-components/attachment-preview/attachment-preview'
import { useGetSignedUploadUrlMutation } from '@common-reducers/backend-api/backend-api'
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook'
import { safeCreateObjectURL, uploadFilePut } from '@common-services/utils'
import { setAsset, setImageId, setIsFileUploading, setLinkPreview, setSendLinkPreview } from '@common/reducers/ScheduledMessageEditorReducer'
import type { Asset } from '@common/types/interface'
import { AudioFile, DeleteOutline, Download, VolumeUp } from '@mui/icons-material'
import { Chip, CircularProgress, IconButton, Paper, Stack, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { ChangeEvent, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
    setSnackbarMessage?: (message: string) => void;
    isPreview?: boolean;
    inputRef?: React.RefObject<HTMLInputElement>;
    onFilesSelected?: (files: File[]) => void;
    hasOverflow?: boolean;
}

const MessageEditorAttachmentBox = ({
    setSnackbarMessage,
    isPreview,
    inputRef: externalInputRef,
    onFilesSelected,
}: Props) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation('common');

    const asset = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.asset);
    const sendLinkPreview = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.sendLinkPreview);

    // File upload state
    const [loadingAttachment, setLoadingAttachment] = useState(false);
    const [getSignedUploadUrl] = useGetSignedUploadUrlMutation();

    const handleAssetChange = useCallback((asset?: Asset, imageId?: string) => {
        dispatch(setAsset(asset));
        dispatch(setImageId(imageId ?? ''));
    }, [dispatch]);

    const handleSelectedFiles = useCallback(async (acceptedFiles: File[]) => {
        console.log('handleSelectedFiles called with:', acceptedFiles);
        const [attachment] = acceptedFiles;

        if (!attachment) {
            console.log('No attachment found');
            setLoadingAttachment(false);
            return;
        }

        // Check file size limit (16 MB = 16777216 bytes - WhatsApp's limit)
        const MAX_FILE_SIZE = 16777216;
        if (attachment.size > MAX_FILE_SIZE) {
            const fileSizeMB = (attachment.size / (1024 * 1024)).toFixed(2);
            const maxSizeMB = (MAX_FILE_SIZE / (1024 * 1024)).toFixed(0);
            setSnackbarMessage?.(`File size (${fileSizeMB} MB) exceeds WhatsApp's limit of ${maxSizeMB} MB. Please choose a smaller file.`);
            setLoadingAttachment(false);
            return;
        }

        console.log('Processing attachment:', attachment.name, attachment.type);

        // wa-scheduler#121: the existing attachment is deliberately NOT cleared
        // here. Clearing up-front meant a failed upload also destroyed the image
        // the user had already attached, leaving them worse off than before they
        // clicked. The asset is replaced below, only once the new upload lands.

        setLoadingAttachment(true);
        dispatch(setIsFileUploading(true));

        try {
            const uploadUrlResponse: { url: string; publicUrl: string } = await getSignedUploadUrl(attachment?.type).unwrap();
            console.log('Upload URL received:', uploadUrlResponse);

            await uploadFilePut(attachment, uploadUrlResponse.url);
            console.log('File uploaded successfully');

            const hadLinkPreview = sendLinkPreview;

            handleAssetChange({
                url: uploadUrlResponse.publicUrl,
                name: attachment.name,
                type: attachment.type,
            });

            console.log('Asset updated in state');

            if (hadLinkPreview) {
                dispatch(setSendLinkPreview(false));
                dispatch(setLinkPreview({}));
                setSnackbarMessage?.('Link preview disabled - WhatsApp only supports one attachment type per message');
            }
        } catch (e) {
            // wa-scheduler#121: this used to console.error only, so an upload
            // that failed left the composer looking idle with no attachment and
            // no explanation — the "silently fails (no error)" half of the bug.
            console.error('Error processing file:', e);
            setSnackbarMessage?.(t('scheduledMessage.validation.uploadFailed'));
        } finally {
            // Must be in a `finally`: these two lines previously sat after the
            // try/catch, so they ran on every *settled* outcome but never at all
            // when an await hung forever — which is what pinned the spinner.
            setLoadingAttachment(false);
            dispatch(setIsFileUploading(false));
        }
    }, [getSignedUploadUrl, handleAssetChange, sendLinkPreview, dispatch, setSnackbarMessage, t]);

    const onChangeFile = (e: ChangeEvent<HTMLInputElement>) => {
        e.preventDefault()
        e.stopPropagation()
        if (e.target.files && e.target.files.length > 0) {
            console.log('Files selected:', e.target.files.length);
            const files = Array.from(e.target.files);
            handleSelectedFiles(files);
            // Also call parent callback if provided
            if (onFilesSelected) {
                onFilesSelected(files);
            }
            // Reset the input value to allow selecting the same file again
            e.target.value = '';
        }
    };

    async function downloadAttachment(attachmentUrl: string, attachmentName: string) {
        try {
            const response = await fetch(attachmentUrl);
            const blob = await response.blob();
            const downloadUrl = safeCreateObjectURL(blob);
            const downloadLink = document.createElement('a');
            downloadLink.href = downloadUrl;
            downloadLink.setAttribute('download', attachmentName || 'download');
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Download failed', error);
        }
    }
    const isAudioAttachment = asset?.type?.includes('audio');

    return (
        <Box>
            {!isPreview && (
                <>
                    {/* Input for button clicks */}
                    <input
                        ref={externalInputRef}
                        type="file"
                        style={{ display: "none" }}
                        onChange={onChangeFile}
                    />
                </>
            )}

            {loadingAttachment && (
                <Box
                    sx={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '4px',
                        m: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                >
                    <CircularProgress size={24} />
                </Box>
            )}


            {/* Audio Attachment UI - Normal Positioning */}
            {asset?.url && !loadingAttachment && isAudioAttachment && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        m: 2,
                        borderRadius: '12px',
                        backgroundColor: 'var(--color-surface)',
                        transition: 'all 0.2s ease-in-out',
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '12px',
                                backgroundColor: '#51b3e2',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                flexShrink: 0,
                            }}
                        >
                            <VolumeUp sx={{ fontSize: 24 }} />
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 500,
                                    color: 'var(--color-text)',
                                    fontSize: '14px',
                                    lineHeight: 1.4,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {asset?.name || 'Audio Message'}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                <Chip
                                    label="Audio"
                                    size="small"
                                    icon={<AudioFile sx={{ fontSize: '14px !important' }} />}
                                    sx={{
                                        height: 20,
                                        fontSize: '11px',
                                        fontWeight: 500,
                                        backgroundColor: 'rgba(81, 179, 226, 0.1)',
                                        color: '#51b3e2',
                                        border: 'none',
                                        '& .MuiChip-icon': {
                                            color: '#51b3e2',
                                        },
                                    }}
                                />
                            </Stack>
                        </Box>

                        <Stack direction="row" spacing={0.5}>
                            <IconButton
                                size="small"
                                onClick={() => downloadAttachment(asset?.url, asset?.name)}
                                sx={{
                                    padding: '6px',
                                    color: 'var(--bulk-text-secondary)',
                                    backgroundColor: 'transparent',
                                    border: '1px solid var(--tasks-cell-border)',
                                    borderRadius: '8px',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        backgroundColor: 'rgba(81, 179, 226, 0.1)',
                                        borderColor: '#51b3e2',
                                        color: '#51b3e2',
                                    }
                                }}
                            >
                                <Download sx={{ fontSize: '16px' }} />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => handleAssetChange(undefined, undefined)}
                                sx={{
                                    padding: '6px',
                                    color: 'var(--bulk-error)',
                                    backgroundColor: 'transparent',
                                    border: '1px solid var(--tasks-cell-border)',
                                    borderRadius: '8px',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        backgroundColor: '#ffebee',
                                        borderColor: 'var(--bulk-error)',
                                    }
                                }}
                            >
                                <DeleteOutline sx={{ fontSize: '16px' }} />
                            </IconButton>
                        </Stack>
                    </Stack>
                </Paper>
            )}

            {/* Non-Audio Attachment UI - Absolute Positioning */}
            {asset?.url && !loadingAttachment && !isAudioAttachment && (
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 10,
                        m: 2,
                        width: '60px',
                        height: '60px',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        '&:hover': {
                            '& .hover-actions': {
                                opacity: 1,
                            },
                        },
                    }}
                >
                    <AttachmentPreview
                        isFromCampaigns={true}
                        attachmentName={asset?.name}
                        attachmentType={asset?.type}
                        attachmentUrl={asset?.url}
                    />
                    <Stack
                        direction="row"
                        spacing={0.5}
                        className="hover-actions"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            opacity: 0,
                            transition: 'opacity 0.2s ease-in-out',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={() => handleAssetChange(undefined, undefined)}
                            sx={{
                                padding: '4px',
                                color: 'white',
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                }
                            }}
                        >
                            <DeleteOutline sx={{ fontSize: '16px' }} />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => downloadAttachment(asset?.url, asset?.name)}
                            sx={{
                                padding: '4px',
                                color: 'white',
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                }
                            }}
                        >
                            <Download sx={{ fontSize: '16px' }} />
                        </IconButton>
                    </Stack>
                </Box>
            )}
        </Box>
    )
}

export default MessageEditorAttachmentBox;
